import { CTA_TYPE_OPTIONS, MLP_NAVBAR_LOGO } from '@/common/constants';
import { CTAProps } from '@/components/CTA';
import React from 'react';
import { useIntl } from 'react-intl';
import { DesktopNavbar } from './DesktopNavbar';
import { MobileNavbar } from './MobileNavbar';
import styles from './Navbar.module.scss';
import { NavbarProps } from './types';

const CREATE_EVENTS_OVERVIEW_PATH = '/organizer/overview/';
const CREATE_EVENTS_SIGNUP_PATH = /\/creator\/onboarding\/guest-start\/?(?=[?#]|$)/;
const TREATMENT_CTA_TRACKING_IDS = {
  createEvents: 'mlp-webapp-navbar-item-create-events',
  contactSales: 'mlp-webapp-navbar-item-contact-sales',
  getStarted: 'mlp-webapp-navbar-item-get-started',
} as const;
const TREATMENT_CTA_MESSAGES = {
  createEvents: {
    id: 'headerCreatorCta.createEvents',
    defaultMessage: 'Create Events',
    description: 'Navbar CTA for starting event creation',
  },
  getStarted: {
    id: 'headerCreatorCta.getStarted',
    defaultMessage: 'Get Started',
    description: 'Navbar CTA for starting creator onboarding',
  },
};
const DEFAULT_TREATMENT_CTA_LABELS = {
  createEvents: 'Create Events',
  getStarted: 'Get Started',
};

type TreatmentCtaLabels = typeof DEFAULT_TREATMENT_CTA_LABELS;

const getOverviewUrl = (createEventsUrl: string) =>
  createEventsUrl.replace(CREATE_EVENTS_SIGNUP_PATH, CREATE_EVENTS_OVERVIEW_PATH);

const isExpectedCreateEventsUrl = (url: string) => CREATE_EVENTS_SIGNUP_PATH.test(url);

const hasExpectedCreatorCta = (cta: CTAProps | undefined, type: string) =>
  typeof cta?.label === 'string' &&
  cta.label.trim().length > 0 &&
  typeof cta.link === 'string' &&
  cta.link.trim().length > 0 &&
  typeof cta.dataHeapId === 'string' &&
  cta.dataHeapId.trim().length > 0 &&
  typeof cta.type === 'string' &&
  cta.type.toLowerCase() === type;

const hasExpectedCreatorCtas = (ctas: CTAProps[]): ctas is [CTAProps, CTAProps] =>
  ctas.length === 2 &&
  hasExpectedCreatorCta(ctas[0], CTA_TYPE_OPTIONS.SECONDARY) &&
  hasExpectedCreatorCta(ctas[1], CTA_TYPE_OPTIONS.PRIMARY) &&
  isExpectedCreateEventsUrl(ctas[1].link);

export const getNavbarCtas = (
  ctas: CTAProps[] | undefined,
  creatorVariant: NavbarProps['creatorVariant'],
  isUserAuthenticated: boolean | undefined,
  treatmentLabels: TreatmentCtaLabels = DEFAULT_TREATMENT_CTA_LABELS,
): CTAProps[] | undefined => {
  if (
    isUserAuthenticated !== false ||
    !ctas ||
    (creatorVariant !== 'restore-2025-layout' && creatorVariant !== 'orange-direct-signup') ||
    !hasExpectedCreatorCtas(ctas)
  ) {
    return ctas;
  }

  const [contactSalesCta, createEventsCta] = ctas;
  const isOrangeTreatment = creatorVariant === 'orange-direct-signup';

  return [
    {
      ...createEventsCta,
      label: treatmentLabels.createEvents,
      link: isOrangeTreatment ? createEventsCta.link : getOverviewUrl(createEventsCta.link),
      type: CTA_TYPE_OPTIONS.PRIMARY,
      dataHeapId: TREATMENT_CTA_TRACKING_IDS.createEvents,
      variant: isOrangeTreatment ? 'orange-text' : 'plain',
    },
    {
      ...contactSalesCta,
      dataHeapId: TREATMENT_CTA_TRACKING_IDS.contactSales,
    },
    {
      ...createEventsCta,
      label: treatmentLabels.getStarted,
      dataHeapId: TREATMENT_CTA_TRACKING_IDS.getStarted,
      ...(isOrangeTreatment && { variant: 'orange-filled' as const }),
    },
  ];
};

export const Navbar: React.FC<NavbarProps> = ({
  title = MLP_NAVBAR_LOGO,
  titleLink = '/',
  navItems,
  ctas,
  creatorVariant,
  isUserAuthenticated,
}) => {
  const { formatMessage } = useIntl();
  const navbarCtas = getNavbarCtas(ctas, creatorVariant, isUserAuthenticated, {
    createEvents: formatMessage(TREATMENT_CTA_MESSAGES.createEvents),
    getStarted: formatMessage(TREATMENT_CTA_MESSAGES.getStarted),
  });

  return (
    <>
      <DesktopNavbar
        title={title}
        titleLink={titleLink}
        navItems={navItems}
        ctas={navbarCtas}
        className={styles.navbar_desktop}
      />
      <MobileNavbar
        title={title}
        titleLink={titleLink}
        navItems={navItems}
        ctas={navbarCtas}
        className={styles.navbar_mobile}
      />
    </>
  );
};
